import { cloneDeep } from 'lodash';
import request from '@/utils/requests';

const getters = {
  isEditingMode: (state) => state.isEditingMode,

  isAddingNewLocation: (state) => state.isAddingNewLocation,

  isEditingLocation: (state) => state.isEditingLocation,

  getEditingLocationIndex: (state) => state.editingLocationIndex,

  getEditingCompany: (state) => state.editingCompany,

  getUpdatedImage: (state) => state.updatedImage,

  getCompanyAddresses: (state) => state.editingCompany.addresses,

  getCompanyToSave: (state) => {
    const company = cloneDeep(state.editingCompany);

    // Ensuring only one address is primary
    if (company.addresses && company.addresses.length > 0) {
      let primarySet = false;
      company.addresses = company.addresses.map((addr) => {
        if (addr.primary && !primarySet) {
          primarySet = true;
          return { ...addr, primary: true };
        }
        return { ...addr, primary: false };
      });
    }

    company.materials_interested = company.materials_interested.map((item) => item.id);
    company.materials_providing = company.materials_providing.map((item) => item.id);

    if (!company.country_calling_code) {
      delete company.phone;
    }

    // eslint-disable-next-line dot-notation
    const updatedImage = cloneDeep(state.updatedImage);
    if (updatedImage) {
      company.image = updatedImage;
      delete company.image_url;
      delete company.image_name;
    }

    // Add new fields
    company.company_license_number = state.editingCompany.company_license_number;
    company.company_license_expiry_date = state.editingCompany.company_license_expiry_date;
    company.website_url = state.editingCompany.website_url;
    company.tax_certificate_number = state.editingCompany.tax_certificate_number;

    // Normalize new fields: empty string -> null
    company.about_info = company.about_info === '' ? null : company.about_info;
    company.company_license_number =
      company.company_license_number === '' ? null : company.company_license_number;
    company.company_license_expiry_date =
      company.company_license_expiry_date === '' ? null : company.company_license_expiry_date;
    company.website_url = company.website_url === '' ? null : company.website_url;
    company.tax_certificate_number =
      company.tax_certificate_number === '' ? null : company.tax_certificate_number;

    return company;
  },
};

const mutations = {
  SET_EDITING_MODE: (state, isEditingMode) => {
    state.isEditingMode = isEditingMode;
  },

  SET_IS_ADDING_NEW_LOCATION: (state, isAddingNewLocation) => {
    state.isAddingNewLocation = isAddingNewLocation;
  },

  SET_IS_EDITING_LOCATION: (state, isEditingLocation) => {
    state.isEditingLocation = isEditingLocation;
  },

  SET_EDITING_LOCATION_INDEX: (state, index) => {
    state.editingLocationIndex = index;
  },

  SET_EDITING_COMPANY: (state, company) => {
    state.editingCompany = company;
  },

  SET_EDITING_COMPANY_ADDRESSES: (state, companyAddresses) => {
    state.editingCompany.addresses = companyAddresses;
  },

  SET_COMPANY_IMAGE: (state, image) => {
    state.updatedImage = image;
  },

  SET_PHONE_COUNTRY_CALLING_CODE: (state, countryCallingCode) => {
    state.editingCompany.country_calling_code = countryCallingCode;
  },

  SET_PHONE_NUMBER: (state, phone) => {
    state.editingCompany.phone = phone;
  },

  SET_COMPANY_SIZE: (state, companySize) => {
    state.editingCompany.company_size = companySize;
  },

  SET_COMPANY_ABOUT_INFO: (state, aboutInfo) => {
    state.editingCompany.about_info = aboutInfo;
  },

  SET_COMPANY_PROVIDING_MATERIALS: (state, providingMaterials) => {
    state.editingCompany.materials_providing = providingMaterials;
  },

  SET_COMPANY_INTERESTED_MATERIALS: (state, interestedMaterials) => {
    state.editingCompany.materials_interested = interestedMaterials;
  },

  SET_COMPANY_LICENSE_NUMBER: (state, value) => {
    state.editingCompany.company_license_number = value;
  },

  SET_COMPANY_LICENSE_EXPIRY_DATE: (state, value) => {
    state.editingCompany.company_license_expiry_date = value;
  },

  SET_COMPANY_WEBSITE_URL: (state, value) => {
    state.editingCompany.website_url = value;
  },

  SET_COMPANY_TAX_CERTIFICATE_NUMBER: (state, value) => {
    state.editingCompany.tax_certificate_number = value;
  },
};

const actions = {
  startEditingMode: async ({ commit, rootGetters, dispatch }) => {
    const userCompanyId = rootGetters['auth/getUserCompanyId'];
    const company = await dispatch(
      'companies/fetchCompanyById',
      { id: userCompanyId },
      { root: true }
    );

    commit('SET_EDITING_COMPANY', company);
    commit('SET_EDITING_MODE', true);
  },

  endEditingMode: ({ commit, dispatch }) => {
    commit('SET_EDITING_COMPANY', {});
    commit('SET_COMPANY_IMAGE', null);
    commit('SET_EDITING_MODE', false);
    dispatch('endAddNewLocation');
    dispatch('endEditLocation');
  },

  startAddNewLocation: ({ commit }) => {
    commit('SET_IS_ADDING_NEW_LOCATION', true);
  },

  endAddNewLocation: ({ commit }) => {
    commit('SET_IS_ADDING_NEW_LOCATION', false);
  },

  startEditLocation: ({ commit }, index) => {
    commit('SET_IS_EDITING_LOCATION', true);
    commit('SET_EDITING_LOCATION_INDEX', index);
  },

  endEditLocation: ({ commit }) => {
    commit('SET_IS_EDITING_LOCATION', false);
    commit('SET_EDITING_LOCATION_INDEX', -1);
  },

  setCompanyAddresses: ({ commit }, addresses) => {
    commit('SET_EDITING_COMPANY_ADDRESSES', addresses);
  },

  setUpdatedImage: ({ commit }, imageData) => {
    commit('SET_COMPANY_IMAGE', imageData);
  },

  setCompanyPhone: ({ commit }, { countryCode, number }) => {
    commit('SET_PHONE_COUNTRY_CALLING_CODE', countryCode);
    commit('SET_PHONE_NUMBER', number);
  },

  setCompanySize: ({ commit }, companySize) => {
    commit('SET_COMPANY_SIZE', companySize);
  },

  setCompanyAboutInfo: ({ commit }, aboutInfo) => {
    commit('SET_COMPANY_ABOUT_INFO', aboutInfo);
  },

  setCompanyProvidingMaterials: ({ commit }, providingMaterials) => {
    commit('SET_COMPANY_PROVIDING_MATERIALS', providingMaterials);
  },

  setCompanyInterestedMaterials: ({ commit }, interestedMaterials) => {
    commit('SET_COMPANY_INTERESTED_MATERIALS', interestedMaterials);
  },

  setCompanyLicenseNumber: ({ commit }, value) => {
    commit('SET_COMPANY_LICENSE_NUMBER', value);
  },
  setCompanyLicenseExpiryDate: ({ commit }, value) => {
    commit('SET_COMPANY_LICENSE_EXPIRY_DATE', value);
  },
  setCompanyWebsiteUrl: ({ commit }, value) => {
    commit('SET_COMPANY_WEBSITE_URL', value);
  },
  setCompanyTaxCertificateNumber: ({ commit }, value) => {
    commit('SET_COMPANY_TAX_CERTIFICATE_NUMBER', value);
  },

  // eslint-disable-next-line no-shadow
  saveCompanyInfo: async ({ dispatch, getters, rootGetters }) => {
    // eslint-disable-next-line dot-notation
    const company = getters['getCompanyToSave'];
    try {
      await request(
        'PUT',
        `api/companies/${company.id}`,
        rootGetters['auth/getToken'],
        company,
        {}
      );
      dispatch('endEditingMode');
      await dispatch('auth/fetchUserCompany', '', { root: true });
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  },
};

const modules = {};

const state = {
  isEditingMode: false,
  isAddingNewLocation: false,
  isEditingLocation: false,
  editingLocationIndex: -1,
  editingCompany: {},
  updatedImage: null,
};

export default {
  namespaced: true,
  getters,
  mutations,
  actions,
  modules,
  state,
};
